/**
 * 付费 cohort（ADM-MTR-S014）共享契约：GET /api/v1/metrics/first-payment-cohorts 的查询参数与响应形态。
 *
 * 以激活为 cohort（dev-docs AD §17，激活口径同 §15.2），统计每个激活用户首次付费落在激活后第几个
 * UTC 自然日（D0 / D1 / D2 / D3-6 / D7-13 / D14+）；每人只计入首付所在桶。观察截止 = 当前时刻。
 */

import { z } from 'zod';
import { ACTIVATION_CHANNEL_TYPES } from './metrics-activation-conversion';
import { metricsCalendarDay, metricsRepeatedListParam } from './metrics-paywall-conversion';

/** 分组维度闭集；country 只在明确请求时才读埋点（页面用于语言行展开到地区） */
export const FIRST_PAYMENT_COHORT_DIMENSIONS = [
  'language',
  'date',
  'app',
  'platform',
  'channel',
  'country',
] as const;
export const firstPaymentCohortDimensionSchema = z.enum(FIRST_PAYMENT_COHORT_DIMENSIONS);
export type FirstPaymentCohortDimension = z.infer<typeof firstPaymentCohortDimensionSchema>;

/**
 * 首付天数分桶（顺序即展示顺序）。matureAfter = 激活后多少个 UTC 日结束后该桶才观察完整
 * （= 桶末天数 + 1；当天未过完不算）；D14+ 无上界，取第 14 天过完后
 */
export const FIRST_PAYMENT_COHORT_BUCKETS = [
  { key: 'd0', label: 'D0', matureAfter: 1 },
  { key: 'd1', label: 'D1', matureAfter: 2 },
  { key: 'd2', label: 'D2', matureAfter: 3 },
  { key: 'd3_6', label: 'D3-6', matureAfter: 7 },
  { key: 'd7_13', label: 'D7-13', matureAfter: 14 },
  { key: 'd14_plus', label: 'D14+', matureAfter: 15 },
] as const;
export type FirstPaymentCohortBucket = (typeof FIRST_PAYMENT_COHORT_BUCKETS)[number]['key'];

/** 单次查询最多跨 366 天（只读到账与归因，不受埋点 90 天保留约束） */
export const FIRST_PAYMENT_COHORT_MAX_DAYS = 366;

/** 分组行返回上限；超出时 `truncated=true` */
export const FIRST_PAYMENT_COHORT_MAX_ROWS = 5000;

/** 以重复查询参数传递的多值参数名（路由按此把同名参数收成数组） */
export const FIRST_PAYMENT_COHORT_LIST_PARAMS = ['app_ids', 'channel_types', 'languages'] as const;

export const firstPaymentCohortQuerySchema = z
  .object({
    from: metricsCalendarDay,
    to: metricsCalendarDay,
    group_by: z
      .string()
      .optional()
      .transform((raw) => [
        ...new Set(
          (raw ?? '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean),
        ),
      ])
      // 缺省 / 空串 / 只有分隔符时按语言分组
      .transform((dims) => (dims.length ? dims : ['language']))
      .pipe(z.array(firstPaymentCohortDimensionSchema).max(FIRST_PAYMENT_COHORT_DIMENSIONS.length)),
    app_ids: metricsRepeatedListParam(z.string().uuid()).transform((ids) =>
      ids.map((id) => id.toLowerCase()),
    ),
    channel_types: metricsRepeatedListParam(z.enum(ACTIVATION_CHANNEL_TYPES)),
    languages: metricsRepeatedListParam(z.string().max(8)),
    min_activations: z.coerce.number().int().min(0).max(100_000_000).optional(),
  })
  // 拒绝未声明参数：误拼的筛选静默丢弃会返回看似合理的全量数据
  .strict()
  .superRefine((query, ctx) => {
    const span =
      (Date.parse(`${query.to}T00:00:00Z`) - Date.parse(`${query.from}T00:00:00Z`)) / 86_400_000;
    if (span < 0) ctx.addIssue({ code: 'custom', path: ['to'], message: 'to 不得早于 from' });
    else if (span + 1 > FIRST_PAYMENT_COHORT_MAX_DAYS)
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: `时间窗最多 ${FIRST_PAYMENT_COHORT_MAX_DAYS} 天`,
      });
  });
export type FirstPaymentCohortQuery = z.infer<typeof firstPaymentCohortQuerySchema>;

/** 分组键：只含请求的 group_by 维度；值为 null = 未知 */
export type FirstPaymentCohortDimensionValues = Partial<
  Record<FirstPaymentCohortDimension, string | null>
>;

/** 激活数 + 累计付费人数 + 各桶首付人数（各桶之和 = payers）+ 各桶已成熟激活数 */
export type FirstPaymentCohortMetrics = {
  activations: number;
  payers: number;
  matured: Record<FirstPaymentCohortBucket, number>;
} & Record<FirstPaymentCohortBucket, number>;

export type FirstPaymentCohortRow = {
  dims: FirstPaymentCohortDimensionValues;
} & FirstPaymentCohortMetrics;

export type FirstPaymentCohortData = {
  window: { from: string; to: string; timezone: 'UTC' };
  /** 观察截止 = 当前时刻 */
  observed_until: string;
  group_by: FirstPaymentCohortDimension[];
  /** 全窗口汇总（每个激活用户只计一次） */
  summary: FirstPaymentCohortMetrics;
  rows: FirstPaymentCohortRow[];
  truncated: boolean;
  /** 筛选候选值：同 App / 时间窗的激活用户中出现过的值（不受其余筛选约束） */
  options: {
    apps: { id: string; name: string; platform: string | null }[];
    languages: string[];
    channels: string[];
  };
  generated_at: string;
};
